import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AspectRatio } from '@/components/ui/aspect-ratio';
import { ExternalLink, Sparkles } from 'lucide-react';
import FavoriteHeart from '@/components/FavoriteHeart';
import SmartImage from '@/components/SmartImage';
import ReviewsSection from '@/components/ReviewsSection';
import { ScrollArea } from '@/components/ui/scroll-area';

const tierLabel = (tier) => (tier === 'premium' ? 'Premium Luxe' : 'Essential Pick');

export const ProductDetailDialog = ({ product, open, onOpenChange }) => {
  if (!product) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-card max-w-3xl p-0 overflow-hidden border-border max-h-[90vh] flex flex-col"
        data-testid="product-detail-drawer"
      >
        <ScrollArea className="flex-1">
        <div className="grid grid-cols-1 md:grid-cols-5">
          <div className="md:col-span-2 bg-cream-paper">
            <AspectRatio ratio={1}>
              <SmartImage src={product.image_url} alt={product.name} fallbackKind="product" className="h-full w-full object-cover" />
            </AspectRatio>
          </div>
          <div className="md:col-span-3 p-6">
            <DialogHeader className="text-left space-y-2">
              <Badge
                className={`w-fit text-[10px] uppercase tracking-[0.14em] font-medium ${
                  product.tier === 'premium'
                    ? 'bg-accent text-ink border border-rose-tint'
                    : 'bg-secondary text-foreground border border-border'
                }`}
              >
                {tierLabel(product.tier)}
              </Badge>
              <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">{product.brand}</p>
              <DialogTitle className="font-display text-2xl sm:text-3xl font-semibold leading-tight">
                {product.name}
              </DialogTitle>
              <DialogDescription className="text-sm text-muted-foreground leading-relaxed">
                {product.description}
              </DialogDescription>
            </DialogHeader>
            <div className="mt-4 flex items-center gap-2 text-xs text-muted-foreground">
              <span className="font-medium text-foreground">{product.price_range}</span>
              <span>·</span>
              <span>${product.price_low}–${product.price_high}</span>
              <span>·</span>
              <span className="capitalize">{product.category}</span>
            </div>
            <div className="mt-5">
              <h4 className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2">Benefits</h4>
              <ul className="space-y-1.5">
                {(product.benefits || []).map((b) => (
                  <li key={b} className="text-sm flex items-start gap-2">
                    <Sparkles className="h-3.5 w-3.5 text-rose-gold mt-0.5 shrink-0" />
                    {b}
                  </li>
                ))}
              </ul>
            </div>
            <div className="mt-5">
              <h4 className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2">How to use</h4>
              <p className="text-sm leading-relaxed text-foreground/90">{product.how_to_use}</p>
            </div>
            <div className="mt-6 flex items-center gap-2">
              <FavoriteHeart
                type="product"
                id={product.id}
                size="lg"
                testId={`product-detail-favorite-${product.id}`}
              />
              <div className="flex-1 rounded-xl border border-border/70 bg-cream-paper px-4 py-2.5 text-xs text-muted-foreground">
                Information only · we don&rsquo;t sell products. Search the brand near you.
              </div>
            </div>
          </div>
        </div>
        <div className="px-6 pb-6">
          <ReviewsSection itemType="product" itemId={product.id} />
        </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default ProductDetailDialog;
